
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { MoreHorizontal, Copy, Ban, ChevronUp, ChevronUpCircle, ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { LicenseStatusBadge } from './LicenseStatusBadge'
import type { License } from '@/lib/types'
import { LicensePlanBadge } from './LicensePlanBadge'

type SortField = 'licenseKey' | 'status' | 'plan' | 'usage' | 'createdAt'
type SortDirection = 'asc' | 'desc'

interface SortState {
  field: SortField
  direction: SortDirection
}

interface LicensesListProps {
  licenses: License[]
  isLoading: boolean
  currentPage: number
  totalPages: number
  itemsPerPage: number
  totalCount: number
  onPageChange: (page: number | ((prev: number) => number)) => void
  onItemsPerPageChange: (count: number) => void
  onRevoke: (id: number) => void
  onCopy: (text: string) => void
  sort?: SortState
  onSort?: (field: SortField) => void
}

// Sortable header component
interface SortableHeaderProps {
  children: React.ReactNode
  field: SortField
  currentSort?: SortState
  onSort?: (field: SortField) => void
  className?: string
}

function SortableHeader({ children, field, currentSort, onSort, className }: SortableHeaderProps) {
  const isSorted = currentSort?.field === field
  const direction = isSorted ? currentSort.direction : null

  return (
    <TableHead
      className={cn(
        'cursor-pointer hover:bg-muted/80 transition-colors select-none',
        isSorted && 'bg-muted/50',
        className
      )}
      onClick={() => onSort?.(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        {isSorted ? (
          direction === 'asc' ? (
            <ChevronUp className="h-3.5 w-3.5 text-primary" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-primary" />
          )
        ) : (
          <ChevronUpCircle className="h-3.5 w-3.5 text-muted-foreground/40" />
        )}
      </div>
    </TableHead>
  )
}

export function LicensesList({
  licenses,
  isLoading,
  currentPage,
  totalPages,
  itemsPerPage,
  totalCount,
  onPageChange,
  onItemsPerPageChange,
  onRevoke,
  onCopy,
  sort,
  onSort,
}: LicensesListProps) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/50 hover:bg-muted/50">
              <SortableHeader
                field="licenseKey"
                currentSort={sort}
                onSort={onSort}
                className="w-[300px]"
              >
                License Key
              </SortableHeader>
              <SortableHeader field="status" currentSort={sort} onSort={onSort}>
                Status
              </SortableHeader>
              <SortableHeader field="plan" currentSort={sort} onSort={onSort}>
                Plan
              </SortableHeader>
              <SortableHeader field="usage" currentSort={sort} onSort={onSort}>
                Usage
              </SortableHeader>
              <SortableHeader field="createdAt" currentSort={sort} onSort={onSort}>
                Created
              </SortableHeader>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              // Loading Skeleton
              [...Array(5)].map((_, i) => (
                <TableRow key={i}>
                  <TableCell><div className="h-4 w-48 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-20 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-24 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-16 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-4 w-32 bg-muted animate-pulse rounded" /></TableCell>
                  <TableCell><div className="h-8 w-8 ml-auto bg-muted animate-pulse rounded" /></TableCell>
                </TableRow>
              ))
            ) : licenses.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                  No licenses found.
                </TableCell>
              </TableRow>
            ) : (
              licenses.map((license) => (
                <TableRow key={license.id}>
                  <TableCell>
                    <div className="flex items-center gap-2 group">
                      <span className="font-mono text-xs font-medium text-foreground/80 bg-muted/50 px-2 py-1 rounded select-all">
                        {license.licenseKey}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => onCopy(license.licenseKey)}
                      >
                        <Copy className="h-3 w-3 text-muted-foreground" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <LicenseStatusBadge license={license} />
                  </TableCell>
                  <TableCell>
                    <LicensePlanBadge plan={license.plan} />
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1 text-xs">
                      <div className="flex justify-between w-[100px] text-muted-foreground">
                        <span>Tenants</span>
                        <span>
                          {license.tenants?.length || 0}
                          <span className="text-muted-foreground/50">/</span>
                          {license.maxTenants === -1 ? '∞' : license.maxTenants}
                        </span>
                      </div>
                      {license.maxTenants !== -1 && (
                        <div className="h-1 w-[100px] bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full bg-primary/50"
                            style={{
                              width: `${Math.min(100, ((license.tenants?.length || 0) / license.maxTenants) * 100)}%`
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="text-sm text-foreground">
                        {new Date(license.createdAt).toLocaleDateString()}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Exp: {new Date(license.expiresAt).toLocaleDateString()}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => onCopy(license.licenseKey)}>
                          <Copy className="mr-2 h-4 w-4" />
                          Copy License Key
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600 focus:bg-red-50"
                          onClick={() => onRevoke(license.id)}
                          disabled={license.isRevoked}
                        >
                          <Ban className="mr-2 h-4 w-4" />
                          Revoke License
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalCount > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, totalCount)} of {totalCount} licenses
          </div>
          <div className="flex items-center gap-2">
            <Select value={String(itemsPerPage)} onValueChange={(v) => onItemsPerPageChange(Number(v))}>
              <SelectTrigger className="w-[70px] h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                    className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>

                {/* Page numbers */}
                {totalPages <= 7 ? (
                  Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => onPageChange(page)}
                        isActive={currentPage === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))
                ) : (
                  <>
                    {currentPage <= 3 ? (
                      <>
                        {Array.from({ length: 5 }, (_, i) => i + 1).map(page => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => onPageChange(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationEllipsis />
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(totalPages)} className="cursor-pointer">
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    ) : currentPage >= totalPages - 2 ? (
                      <>
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(1)} className="cursor-pointer">
                            1
                          </PaginationLink>
                        </PaginationItem>
                        <PaginationEllipsis />
                        {Array.from({ length: 5 }, (_, i) => totalPages - 4 + i).map(page => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => onPageChange(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                      </>
                    ) : (
                      <>
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(1)} className="cursor-pointer">
                            1
                          </PaginationLink>
                        </PaginationItem>
                        <PaginationEllipsis />
                        {Array.from({ length: 3 }, (_, i) => currentPage - 1 + i).map(page => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => onPageChange(page)}
                              isActive={currentPage === page}
                              className="cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationEllipsis />
                        <PaginationItem>
                          <PaginationLink onClick={() => onPageChange(totalPages)} className="cursor-pointer">
                            {totalPages}
                          </PaginationLink>
                        </PaginationItem>
                      </>
                    )}
                  </>
                )}

                <PaginationItem>
                  <PaginationNext
                    onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                    className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}
    </div>
  )
}
